// ============================================================
// THE COMPANY — KnowledgeGaps Component (Board View tab)
// ============================================================

import { useMemo, useState, useCallback } from 'react';
import { useCompanyStore } from '../../store/useCompanyStore';
import type { Decision, Role } from '../../lib/types';

interface RoleGap {
  role: Role;
  missingDecisions: Decision[];
}

export default function KnowledgeGaps() {
  const decisions = useCompanyStore((s) => s.decisions);
  const roles = useCompanyStore((s) => s.roles);

  const [expandedRoleId, setExpandedRoleId] = useState<string | null>(null);

  const toggleExpand = useCallback((roleId: string) => {
    setExpandedRoleId((prev) => (prev === roleId ? null : roleId));
  }, []);

  // Compute gaps: for each role, find decisions they should know about but don't
  const gaps: RoleGap[] = useMemo(() => {
    const result: RoleGap[] = [];

    for (const role of roles) {
      const missing = decisions.filter(
        (d) =>
          d.roleId !== role.id && !d.informedRoleIds.includes(role.id),
      );

      if (missing.length > 0) {
        result.push({ role, missingDecisions: missing });
      }
    }

    // Sort by most missing first (highest urgency)
    result.sort((a, b) => b.missingDecisions.length - a.missingDecisions.length);
    return result;
  }, [decisions, roles]);

  // No gaps state
  if (gaps.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <svg
          className="h-10 w-10 mb-3"
          style={{ color: '#22c55e' }}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p className="text-sm font-medium" style={{ color: '#22c55e' }}>
          All roles are fully briefed!
        </p>
        <p
          className="text-xs mt-1"
          style={{ color: 'var(--text-muted)' }}
        >
          {decisions.length === 0
            ? 'No decisions have been recorded yet.'
            : 'Every role has been informed about all relevant decisions.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {gaps.map(({ role, missingDecisions }) => {
        const isExpanded = expandedRoleId === role.id;
        const isUrgent = missingDecisions.length >= 3;

        return (
          <div
            key={role.id}
            className="rounded-lg overflow-hidden"
            style={{
              backgroundColor: 'var(--bg-elevated)',
              border: `1px solid ${isUrgent ? 'rgba(239, 68, 68, 0.3)' : 'var(--border-subtle)'}`,
            }}
          >
            {/* Header — clickable to expand */}
            <button
              type="button"
              onClick={() => toggleExpand(role.id)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors duration-150 hover:bg-[var(--bg-surface)]"
            >
              {/* Expand/collapse chevron */}
              <svg
                className={`h-3.5 w-3.5 shrink-0 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
                style={{ color: 'var(--text-muted)' }}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8.25 4.5l7.5 7.5-7.5 7.5"
                />
              </svg>

              {/* Role info */}
              <div className="flex-1 min-w-0">
                <span
                  className="text-sm font-medium"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {role.name}
                </span>
                <span
                  className="text-sm ml-1.5"
                  style={{ color: 'var(--text-muted)' }}
                >
                  ({role.title})
                </span>
              </div>

              {/* Missing count badge */}
              <span
                className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold shrink-0"
                style={{
                  backgroundColor: isUrgent
                    ? 'rgba(239, 68, 68, 0.15)'
                    : 'rgba(245, 158, 11, 0.15)',
                  color: isUrgent ? '#ef4444' : '#f59e0b',
                  border: `1px solid ${isUrgent ? 'rgba(239, 68, 68, 0.25)' : 'rgba(245, 158, 11, 0.25)'}`,
                }}
              >
                missing {missingDecisions.length} decision
                {missingDecisions.length !== 1 ? 's' : ''}
              </span>
            </button>

            {/* Expanded: list missing decisions */}
            {isExpanded && (
              <div
                className="px-4 pb-3 pt-0 space-y-2 border-t"
                style={{ borderColor: 'var(--border-subtle)' }}
              >
                <div className="pt-3 space-y-2">
                  {missingDecisions.map((decision) => {
                    const maker = roles.find((r) => r.id === decision.roleId);
                    const dateStr = new Date(
                      decision.createdAt,
                    ).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                    });

                    const truncated =
                      decision.content.length > 120
                        ? decision.content.slice(0, 120) + '...'
                        : decision.content;

                    return (
                      <div
                        key={decision.id}
                        className="flex items-start gap-2.5 rounded-md px-3 py-2"
                        style={{
                          backgroundColor: 'var(--bg-surface)',
                          border: '1px solid var(--border-subtle)',
                        }}
                      >
                        <span
                          className="mt-0.5 shrink-0 text-xs"
                          style={{ color: '#ef4444' }}
                        >
                          &#10005;
                        </span>
                        <div className="min-w-0 flex-1">
                          <p
                            className="text-sm leading-snug"
                            style={{ color: 'var(--text-primary)' }}
                          >
                            {truncated}
                          </p>
                          <p
                            className="text-[11px] mt-1"
                            style={{ color: 'var(--text-muted)' }}
                          >
                            {maker ? maker.name : 'Unknown'} &middot; {dateStr}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
